<template>
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMeta :prepareGoogleAds="prepareGoogleAds" />
    </LazyHydration>
    <template v-if="pageStatus === 200 || manuallyHandledError">
        <component
            :is="isLayoutVisible ? Layout : 'div'"
            v-if="noLazyHydration"
        >
            <RouterView />
        </component>
        <LazyHydration
            v-else
            :hydrate="hydrateOnIdle()"
        >
            <component :is="isLayoutVisible ? Layout : 'div'">
                <RouterView />
            </component>
        </LazyHydration>
    </template>
    <Layout v-else>
        <ErrorPage
            v-if="pageStatus >= 400 && pageStatus < 500"
            :type="404"
        />
        <ErrorPage
            v-else-if="pageStatus >= 500"
            :type="500"
        />
    </Layout>
    <ModalSignIn />
    <ModalPayment />
    <template v-if="!appStore.isOppoWL">
        <ModalInstallGooglePlay />
        <ModalPlayOnApp />
    </template>
    <ModalAdDisabled />
    <DebugDialog />
    <PageLoadBar />
</template>
<script lang="ts" setup>
// необходимый для SSR и правильных статусов ответов враппер
import { storeToRefs } from 'pinia'
import {
    computed,
    hydrateOnIdle,
    onBeforeMount,
    onMounted,
    onServerPrefetch,
    onUnmounted,
    ref,
    useSSRContext,
    watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import HeadMeta from '@/components_new/HeadMeta/HeadMeta.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import { DebugDialog } from '@/components_new/Modals/async/DebugDialog'
import { ModalAdDisabled } from '@/components_new/Modals/async/ModalAdDisabled'
import { ModalInstallGooglePlay } from '@/components_new/Modals/async/ModalInstallGooglePlay'
import { ModalPayment } from '@/components_new/Modals/async/ModalPayment'
import { ModalPlayOnApp } from '@/components_new/Modals/async/ModalPlayOnApp'
import { ModalSignIn } from '@/components_new/Modals/async/ModalSignIn'
import PageLoadBar from '@/components_new/PageLoadBar.vue'
import { useGoogleProvidersEnabled } from '@/composables/ad-providers/use-google-providers-enabled'
import Layout from '@/layouts/Layout.vue'
import ErrorPage from '@/pages/Error/ErrorPage.vue'
import { ROUTE_NAMES, ROUTES_WITH_GAME_PARAM } from '@/router/constants'
import { ALL_EXPERIMENTS_CONFIG } from '@/server/experiments/config/config'
import { getExperimentOverrides } from '@/server/experiments/getExperimentOverrides/getExperimentOverrides'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useGlobalInterfaceEvents, useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { type PlatformType, type ServerRenderContext } from '@/types'
import {
    getAUID,
    getContentScopeFromCookies,
    getPlatformFromCookies,
    getRankingParamsFromCookies,
    getUACFromCookies30d,
} from '@/utils/cookie-parser'
import { getCurrentPlatform } from '@/utils/helpers'

import { useClsTracking } from './composables/use-cls-tracking'
import { TargetingMapping } from './modules/adv/targgeting-mapping'
import { AUDIENCE_PARAM, GAME_DISTRIBUTION_GAME_ID_PARAM } from './router/query-params'
import { useConsentStore } from './store/consent-store'
import { useLayoutStore } from './store/layout-store'
import { useCtrLoggerStore } from './store/logger/use-ctr-logger-store'
import { useModalInstallGooglePlayStore } from './store/modal-install-google-play-store'
import { useModalPlayOnAppStore } from './store/modal-play-on-app-store'
import { useNativeAppStore } from './store/native-app-store'
import { useNotificationsQueue } from './store/notification-que/notifications-queue'
import { usePushSubscriptionStore } from './store/push-subscribe/store'
import { usePWAInstallStore } from './store/pwa-install/store'
import { installVueConsoleHandlers } from './utils/vue-console-handler'

useGlobalInterfaceEvents()
const appStore = useAppStore()
const ctrLogger = useCtrLoggerStore()
const nativeAppSDK = useNativeAppStore()

const adsStore = useAdsStore()
const adBlockStore = useAdBlockStore()
const loggerStore = useLoggerStore()
const notificationsQueue = useNotificationsQueue()
const consentStore = useConsentStore()
const $router = useRouter()
const $route = useRoute()
const pwaStore = usePWAInstallStore()
const pushSubscriptionStore = usePushSubscriptionStore()
const layoutStore = useLayoutStore()
const modalInstallGooglePlayStore = useModalInstallGooglePlayStore()
const modalPlayOnAppStore = useModalPlayOnAppStore()
const { fetchGoogleProvidersEnabled, isGoogleProvidersEnabled } = useGoogleProvidersEnabled()

const { pageStatus, platform: platformFromStore, isGameDistribution } = storeToRefs(appStore)
const isLayoutVisible = computed(() => !$route.meta.noLayout)
const noLazyHydration = computed(() => $route.meta.noLazyHydration)

function nativeAppUnlockOrientation() {
    if ($route.name !== ROUTE_NAMES.EXPORT_GAME_PAGE) {
        nativeAppSDK.postMessage('changeOrientation', { action: 'unlock' })
    }
}

function sendOneSignalAuidToNative() {
    const auid = appStore.auid?.trim()
    if (!auid || !nativeAppSDK.isAvailable) {
        return
    }

    nativeAppSDK.postMessage('oneSignal.event', { auid })
}

function isNewUser(auidTimestamp: number) {
    return Date.now() - auidTimestamp < 1000 * 60 * 60 * 24
}

const userStore = useUserStore()

const manuallyHandledError = computed(() => $route.meta.manuallyHandledErrors)

const prepareGoogleAds = computed(() => {
    return !isGameDistribution.value
})

useClsTracking({
    threshold: 0.25,
    onHighCls: ({ cls, viewport, sources }) => {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'cls_high',
            action: 'portal_game',
            dimension1: cls.toFixed(4),
            dimension2: viewport,
            dimension3: sources,
            // tmp dirty to log more details
            adUnit: screen.orientation.type,
            adProvider: String(window.scrollY),
        })
    },
})

onServerPrefetch(async () => {
    const ctx = useSSRContext<ServerRenderContext>()

    if (!ctx) {
        return
    }

    if (ctx.pageStatus) {
        appStore.setPageStatus(ctx.pageStatus)
    } else {
        userStore.hasAuthToken = ctx.hasAuthToken
        if (!ctx.hasAuthToken) {
            userStore._isUserInfoLoaded = true
        }

        appStore.setPlatform(ctx.platform)
        appStore.setUserAgent(ctx.userAgent)
        appStore.setAuidInfo(ctx.auid)
        appStore.setExperiments(ctx.experiments)
        appStore.setRankingParams(
            $route.query[AUDIENCE_PARAM]
                ? {
                      audience: $route.query[AUDIENCE_PARAM] as string | undefined,
                  }
                : {
                      clusterId: ctx.rankingParams.clusterId,
                      audience: ctx.rankingParams.audience,
                  },
        )
    }

    appStore.setUrlDetails({
        host: ctx.host,
        query: $route.query,
        referer: ctx.referer,
    })

    appStore.setContentScopeCookie(ctx.contentScope)
    appStore.setLandingAttributesCookie(ctx.landingAttributesCookie)

    await fetchGoogleProvidersEnabled()
})

// Doing all app initialization here, no risk for SSR hydration mismatch
// eslint-disable-next-line no-restricted-syntax
onBeforeMount(() => {
    window.dataLayer = window.dataLayer || []
    window.OneSignalDeferred = window.OneSignalDeferred || []
    appStore.setEffectiveType()
    loggerStore.setCTRSampling()
    nativeAppUnlockOrientation()
    // log initial navigation with current url
    loggerStore.logEvent({
        event: 'user_time',
        eventName: 'navigation',
        dimension1: window.location.href,
    })

    // beforeEach to do work before next page mounts
    $router.beforeEach((to) => {
        appStore.setPageStatus(200)
        // reset content_category and provider for upcoming page to avoid misleading targeting data
        const newAdTargeting = {
            content_category: '',
            provider: '',
            hru: to.name && ROUTES_WITH_GAME_PARAM.includes(to.name as string) ? (to.params.game as string) : '',
            page: to.name as string,
            effectiveType: appStore.effectiveType as string,
        }

        adsStore.updateTargeting(newAdTargeting)
        nativeAppUnlockOrientation()
    })

    $router.afterEach(() => {
        // log following navigations
        loggerStore.logEvent({
            event: 'user_time',
            eventName: 'navigation',
            dimension1: window.location.href,
        })
    })

    window.addEventListener('beforeunload', () => {
        loggerStore.logEvent({
            event: 'user_time',
            eventName: 'portal_session_end',
        })
    })

    // dev-mode store initialization
    appStore.setUserAgent(window.navigator.userAgent)
    appStore.setAuidInfo(getAUID(document.cookie))

    const rankingParamsFromCookie = getRankingParamsFromCookies(document.cookie)
    const rawRankingParams = $route.query[AUDIENCE_PARAM]
        ? {
              audience: $route.query[AUDIENCE_PARAM] as string | undefined,
          }
        : {
              clusterId: rankingParamsFromCookie.clusterId,
              audience: ($route.query[AUDIENCE_PARAM] as string | undefined) || rankingParamsFromCookie.audience,
          }
    appStore.setRankingParams(rawRankingParams)
    appStore.persistRankingParams(rawRankingParams)

    // Use platform from cookie if available (set by server-side iPad detection), otherwise detect client-side
    const platformFromCookie = getPlatformFromCookies(document.cookie)
    appStore.setPlatform((platformFromCookie as PlatformType) || getCurrentPlatform(window.navigator.userAgent))

    // Dev mode runs without SSR, where experiments are normally assigned,
    // so apply ?exp_<name>=<sample> overrides on the client
    if (process.env.NODE_ENV !== 'production') {
        const experimentOverrides = getExperimentOverrides(
            $route.query as Record<string, string>,
            ALL_EXPERIMENTS_CONFIG,
        )
        appStore.setExperiments(
            Object.fromEntries(Object.entries(experimentOverrides).map(([name, sample]) => [name, { sample }])),
        )
    }

    // TODO SSR
    appStore.setUserParams({
        userAcquisitionParams: getUACFromCookies30d(document.cookie),
    })
    appStore.setContentScopeCookie(getContentScopeFromCookies(document.cookie))
    appStore.updateLandingAttributes()

    // Here comes a tricky hack
    // So when no host is set is dev mode which comes without SSR, so we have to set host on client
    // Hash part of the URL isn't presented in SSR at all, but the client-side router can (and does) parse
    // query string in hash mode too
    // So here comes a mismatch between server and client query which affects all query params in hash navigation mode
    // which we use in PWA in TWA
    if (!appStore.host || Boolean(window.location.hash)) {
        appStore.setUrlDetails({
            host: window.location.host,
            query: $route.query,
            referer: document.referrer,
        })
    }
    const GAME_DISTRIBUTION_GAME_ID = $route.query[GAME_DISTRIBUTION_GAME_ID_PARAM]
    if (GAME_DISTRIBUTION_GAME_ID) {
        // TODO pass directly to modules/gd-connector.ts
        window.gd_id = GAME_DISTRIBUTION_GAME_ID as string
    }

    adsStore.setAdServiceConfig({
        // TODO make updateTargeting work with initial before/afterRoute
        targeting: {
            hru: ($route.name && ROUTES_WITH_GAME_PARAM.includes($route.name as string) && $route.params.game) || '',
            page: $route.name as string,
            effectiveType: appStore.effectiveType as string,
            cohort: appStore.cohort || '',
            rankingAudience: `${appStore.rankingParams?.audience || 'NOT_SET'}-${appStore.platform}`,
        },
        auid: appStore.auid || '',
        isGoogleProvidersEnabled: isGoogleProvidersEnabled.value,
    })
    adsStore.adService?.consentManager.ready.then(() => {
        adBlockStore.detectAdBlockStatus(loggerStore.logEvent)
    })

    window.dataLayer.push({
        device_platform: platformFromStore.value,
        new_user: isNewUser(appStore.auidTimestamp),
    })

    let hydrationProblemReported = false
    installVueConsoleHandlers((type, msg) => {
        // log only first hydration related warning
        if (msg.toLowerCase().includes('hydration')) {
            if (hydrationProblemReported) {
                return
            }
            hydrationProblemReported = true
        }
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'vue_message',
            dimension1: type,
            dimension2: msg,
        })
    })
})

const adBlockDetectionIntervalId = ref<number>()

const { user } = storeToRefs(userStore)
watch(
    // if watching user.value it will trigger every time we re-fetch user and it gets re-assigned
    // but watching ._id we actually make it trigger when user login/logoff
    () => user.value?._id,
    () => {
        if (user.value) {
            appStore.setAuidInfo(getAUID(document.cookie))

            window.dataLayer.push({
                user_id: user.value._id,
                new_user: isNewUser(appStore.auidTimestamp),
            })
        }

        adsStore.updateTargeting({
            [TargetingMapping.IS_LOGGED_IN]: user.value ? '1' : '0',
        })
    },
)

watch(
    () => consentStore.isHidden,
    (isHidden) => {
        if (isHidden) {
            notificationsQueue.init()
        } else {
            notificationsQueue.dispose()
        }
    },
)

watch(
    () => appStore.auid,
    () => {
        sendOneSignalAuidToNative()
    },
)

function onVisibilityChanged() {
    layoutStore.setTabFocused(document.visibilityState === 'visible')
}

function onOrientationChange() {
    layoutStore.setOrientation(screen.orientation.type)
    loggerStore.logInterfaceEvents({
        eventName: 'interface_rotate',
    })
}

onMounted(async () => {
    consentStore.init()
    pwaStore.init()
    pushSubscriptionStore.init()
    nativeAppSDK.init()
    sendOneSignalAuidToNative()
    appStore.initPageLoadProgressTracking()

    // this enables css :active pseudo-class on touch devices
    const noop = () => {}
    document.body.addEventListener('touchstart', noop, false)

    document.addEventListener('visibilitychange', onVisibilityChanged)

    layoutStore.setOrientation(screen.orientation.type)
    screen.orientation.addEventListener('change', onOrientationChange)
    modalInstallGooglePlayStore.init()
    if (!appStore.isOppoWL) {
        modalPlayOnAppStore.init()
    }

    await Promise.allSettled([userStore.getUser()])
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'portal_session_start',
        dimension3: appStore.effectiveType as string,
    })
})

onUnmounted(() => {
    if (adBlockDetectionIntervalId.value) {
        clearInterval(adBlockDetectionIntervalId.value)
    }
    modalInstallGooglePlayStore.dispose()
    modalPlayOnAppStore.dispose()
    consentStore.dispose()
    ctrLogger.disconnect()
    nativeAppSDK.dispose()

    document.removeEventListener('visibilitychange', onVisibilityChanged)
    screen.orientation.removeEventListener('change', onOrientationChange)
})
</script>
